import React, { useState, useEffect } from 'react';
import { 
  Shield, Lock, Unlock, Save, RotateCcw, AlertCircle, 
  CheckCircle, Info, Settings 
} from 'lucide-react';
import { apiCall } from './Api';
import { getCurrentToken } from './SessionManager';

const RolePermissionsManager = ({ user }) => {
  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [selectedRole, setSelectedRole] = useState('host_admin');

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const token = getCurrentToken();
      
      const response = await apiCall('/api/auth/module-permissions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setModules(data.modules);
        setPermissions(data.permissions);
        setChanges({});
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (role, moduleKey, action, value) => {
    const changeKey = `${role}-${moduleKey}`;
    
    setChanges(prev => ({
      ...prev,
      [changeKey]: {
        ...prev[changeKey],
        role,
        module_key: moduleKey,
        [action]: value
      }
    }));
  };

  const saveChanges = async () => {
    try {
      setSaving(true);
      const token = getCurrentToken();
      
      // Save each change
      for (const change of Object.values(changes)) {
        const currentPerms = permissions[change.role]?.[change.module_key] || {};
        
        const payload = {
          role: change.role,
          module_key: change.module_key,
          can_view: change.can_view ?? currentPerms.can_view ?? false,
          can_create: change.can_create ?? currentPerms.can_create ?? false,
          can_edit: change.can_edit ?? currentPerms.can_edit ?? false,
          can_delete: change.can_delete ?? currentPerms.can_delete ?? false,
          can_execute: change.can_execute ?? currentPerms.can_execute ?? false
        };
        
        await apiCall('/api/auth/module-permissions/update', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }
      
      // Reload permissions
      await loadPermissions();
      alert('Permissions updated successfully');
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    setChanges({});
  };

  const getPermissionValue = (role, moduleKey, action) => {
    const changeKey = `${role}-${moduleKey}`;
    if (changes[changeKey] && changes[changeKey][action] !== undefined) {
      return changes[changeKey][action];
    }
    return permissions[role]?.[moduleKey]?.[action] ?? false;
  };

  const isFixed = (role, moduleKey) => {
    return permissions[role]?.[moduleKey]?.is_fixed ?? false;
  };

  const hasChanges = Object.keys(changes).length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const roles = [
    { key: 'host_admin', label: 'Admin', color: 'blue' },
    { key: 'host_user', label: 'User', color: 'green' },
    { key: 'host_customer', label: 'Customer', color: 'green' },
    { key: 'host_agent', label: 'Agent', color: 'green' },
    { key: 'host_app', label: 'App', color: 'purple' }
  ];

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Role Permissions Manager</h1>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {hasChanges && (
                <>
                  <button
                    onClick={resetChanges}
                    className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span>Reset</span>
                  </button>
                  <button
                    onClick={saveChanges}
                    disabled={saving}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">

        {/* Role Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b">
            <div className="flex space-x-1 px-6 pt-4">
              {roles.map(role => (
                <button
                  key={role.key}
                  onClick={() => setSelectedRole(role.key)}
                  className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
                    selectedRole === role.key
                      ? `bg-${role.color}-100 text-${role.color}-700 border-b-2 border-${role.color}-600`
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>

          {/* Permissions Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Module
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    View
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Create
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Edit
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delete
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Execute
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {modules.map(module => {
                  const fixed = isFixed(selectedRole, module.module_key);
                  const isSystemModule = module.is_system;
                  const isAdminOnly = module.admin_only;
                  const rolePerms = permissions[selectedRole]?.[module.module_key];
                  const hasAccess = rolePerms?.can_view || false;

                  // Skip admin-only modules for non-admin roles
                  if (isAdminOnly && selectedRole !== 'admin') {
                    return null;
                  }

                  return (
                    <tr key={module.module_key} className={!hasAccess && !fixed ? 'bg-gray-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {fixed ? (
                            <Lock className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Unlock className="h-4 w-4 text-gray-400" />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {module.module_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {module.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      {['can_view', 'can_create', 'can_edit', 'can_delete', 'can_execute'].map(action => (
                        <td key={action} className="px-6 py-4 whitespace-nowrap text-center">
                          <input
                            type="checkbox"
                            checked={getPermissionValue(selectedRole, module.module_key, action)}
                            onChange={(e) => handlePermissionChange(selectedRole, module.module_key, action, e.target.checked)}
                            disabled={fixed || isSystemModule}
                            className={`h-4 w-4 rounded border-gray-300 ${
                              fixed || isSystemModule
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-blue-600 focus:ring-blue-500 cursor-pointer'
                            }`}
                          />
                        </td>
                      ))}
                      
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {fixed ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Fixed
                          </span>
                        ) : isSystemModule ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            System
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Configurable
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionsManager;